import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  CreditCard,
  Phone,
  Add,
  Delete,
  Edit,
  CheckCircle,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';



const Settings = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [paymentForm, setPaymentForm] = useState({
    type: '',
    name: '',
    number: '',
    expiry: '',
    cvv: '',
    provider: '',
  });

  // Load payment methods from localStorage on component mount
  useEffect(() => {
    const savedMethods = localStorage.getItem(`paymentMethods_${partnerId}`);
    const savedSelected = localStorage.getItem(`selectedPaymentMethod_${partnerId}`);
    
    if (savedMethods) {
      setPaymentMethods(JSON.parse(savedMethods));
    }
    if (savedSelected) {
      setSelectedMethod(savedSelected);
    }
  }, [partnerId]);

  // Save payment methods to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`paymentMethods_${partnerId}`, JSON.stringify(paymentMethods));
  }, [paymentMethods, partnerId]);

  // Save selected method to localStorage whenever it changes
  useEffect(() => {
    if (selectedMethod) {
      localStorage.setItem(`selectedPaymentMethod_${partnerId}`, selectedMethod);
    }
  }, [selectedMethod, partnerId]);

  const handleAddPayment = () => {
    if (!paymentForm.type || !paymentForm.name || !paymentForm.number) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    const newMethod = {
      id: Date.now().toString(),
      ...paymentForm,
      isDefault: paymentMethods.length === 0,
    };

    const updatedMethods = [...paymentMethods, newMethod];
    setPaymentMethods(updatedMethods);
    
    // Set as default if it's the first method
    if (paymentMethods.length === 0) {
      setSelectedMethod(newMethod.id);
    }

    setShowAddDialog(false);
    setPaymentForm({
      type: '',
      name: '',
      number: '',
      expiry: '',
      cvv: '',
      provider: '',
    });
    setSuccessMessage('Payment method added successfully!');
    setErrorMessage('');
    
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEditPayment = () => {
    if (!paymentForm.type || !paymentForm.name || !paymentForm.number) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    const updatedMethods = paymentMethods.map(method => 
      method.id === editingMethod.id 
        ? { ...method, ...paymentForm }
        : method
    );
    
    setPaymentMethods(updatedMethods);
    setShowAddDialog(false);
    setEditingMethod(null);
    setPaymentForm({
      type: '',
      name: '',
      number: '',
      expiry: '',
      cvv: '',
      provider: '',
    });
    setSuccessMessage('Payment method updated successfully!');
    setErrorMessage('');
    
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeletePayment = (methodId) => {
    const updatedMethods = paymentMethods.filter(method => method.id !== methodId);
    setPaymentMethods(updatedMethods);
    
    // If deleted method was selected, select first available method
    if (selectedMethod === methodId) {
      const newSelected = updatedMethods.length > 0 ? updatedMethods[0].id : '';
      setSelectedMethod(newSelected);
    }
    
    setSuccessMessage('Payment method deleted successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSetDefault = (methodId) => {
    setSelectedMethod(methodId);
    setSuccessMessage('Default payment method updated!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const openAddDialog = () => {
    setShowAddDialog(true);
    setEditingMethod(null);
    setPaymentForm({
      type: '',
      name: '',
      number: '',
      expiry: '',
      cvv: '',
      provider: '',
    });
    setErrorMessage('');
  };

  const openEditDialog = (method) => {
    setEditingMethod(method);
    setPaymentForm({
      type: method.type,
      name: method.name,
      number: method.number,
      expiry: method.expiry || '',
      cvv: method.cvv || '',
      provider: method.provider || '',
    });
    setShowAddDialog(true);
    setErrorMessage('');
  };

  const closeDialog = () => {
    setShowAddDialog(false);
    setEditingMethod(null);
    setPaymentForm({
      type: '',
      name: '',
      number: '',
      expiry: '',
      cvv: '',
      provider: '',
    });
    setErrorMessage('');
  };

  const getPaymentIcon = (type, provider) => {
    if (type === 'card') {
      return provider === 'visa' ? '💳' : '💳';
    } else if (type === 'mobile_money') {
      return provider === 'airtel' ? '📱' : '📱';
    }
    return '💳';
  };

  const getPaymentDisplayName = (type, provider) => {
    if (type === 'card') {
      return provider === 'visa' ? 'Visa Card' : 'Mastercard';
    } else if (type === 'mobile_money') {
      return provider === 'airtel' ? 'Airtel Money' : 'MTN Mobile Money';
    }
    return 'Payment Method';
  };

  const maskCardNumber = (number) => {
    if (number.length <= 4) return number;
    return '•'.repeat(number.length - 4) + number.slice(-4);
  };

  const maskPhoneNumber = (number) => {
    if (number.length <= 4) return number;
    return number.slice(0, 4) + '•'.repeat(number.length - 6) + number.slice(-2);
  };

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 4, 
      bgcolor: '#ffffff', 
      minHeight: '100vh',
      background: '#ffffff'
    }}>
      {/* Header */}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Payment Methods Section */}
      <Paper sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f92955',
        background: '#fff'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CreditCard sx={{ mr: 2, color: '#f92955', fontSize: 24 }} />
            <Typography variant="h5" sx={{ color: '#000000', fontWeight: 600 }}>
              Payment Methods
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAddDialog}
            sx={{
              bgcolor: '#f92955',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#BBFFC1',
              },
            }}
          >
            Add Payment Method
          </Button>
        </Box>

        {paymentMethods.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            color: '#000000'
          }}>
            <CreditCard sx={{ fontSize: 48, color: '#f92955', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#000000', mb: 1 }}>
              No Payment Methods
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', mb: 3 }}>
              Add your first payment method to get started
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={openAddDialog}
              sx={{
                borderColor: '#f92955',
                color: '#000000',
                '&:hover': {
                  borderColor: '#BBFFC1',
                  bgcolor: '#f9295515',
                },
              }}
            >
              Add Payment Method
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} sm={6} md={4} key={method.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: selectedMethod === method.id ? '2px solid #f92955' : '1px solid #BBFFC1',
                    background: selectedMethod === method.id ? '#f9295515' : '#BBFFC1',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: selectedMethod === method.id 
                        ? 'linear-gradient(90deg, #f92955, #BBFFC1)' 
                        : 'linear-gradient(90deg, #BBFFC1, #f92955)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ 
                        mr: 2, 
                        fontSize: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: selectedMethod === method.id ? '#f9295515' : '#BBFFC1',
                      }}>
                        {getPaymentIcon(method.type, method.provider)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ 
                          color: '#000000', 
                          fontWeight: 600,
                          fontSize: '1rem',
                          mb: 0.5
                        }}>
                          {getPaymentDisplayName(method.type, method.provider)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000000', fontSize: '0.85rem' }}>
                          {method.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(method)}
                          sx={{ color: '#f92955', '&:hover': { color: '#BBFFC1' } }}
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePayment(method.id)}
                          sx={{ color: '#f92955', '&:hover': { color: '#BBFFC1' } }}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
                        {method.type === 'card' ? 'Card Number' : 'Phone Number'}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: '#000000', 
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}>
                        {method.type === 'card' 
                          ? maskCardNumber(method.number)
                          : maskPhoneNumber(method.number)
                        }
                      </Typography>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      bgcolor: '#BBFFC1',
                      p: 1.5,
                      borderRadius: 2
                    }}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={selectedMethod === method.id}
                            onChange={() => handleSetDefault(method.id)}
                            sx={{
                              color: '#000000',
                              '&.Mui-checked': {
                                color: '#f92955',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: '#000000', fontWeight: 600 }}>
                            {selectedMethod === method.id ? 'Default' : 'Set as Default'}
                          </Typography>
                        }
                      />
                      {selectedMethod === method.id && (
                        <Chip
                          label="Default"
                          icon={<CheckCircle />}
                          size="small"
                          sx={{
                            bgcolor: '#BBFFC1',
                            color: '#000',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add/Edit Payment Method Dialog */}
      <Dialog 
        open={showAddDialog} 
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            background: 'linear-gradient(135deg, #BBFFC1 0%, #f92955 100%)',
            border: '1px solid rgba(249, 41, 85, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #f92955, #BBFFC1, #f92955)',
            }
          }
        }}
        TransitionProps={{
          enter: {
            transform: 'scale(1)',
            opacity: 1,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          },
          exit: {
            transform: 'scale(0.9)',
            opacity: 0,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #BBFFC1 0%, #f92955 100%)',
          color: '#fff',
          fontWeight: 700,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 4,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
          }
        }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}>
            <CreditCard sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
              {editingMethod ? 'Update your payment method details' : 'Add a new payment method to your account'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 4 }}>
            {/* Payment Type Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ 
                color: '#000000', 
                fontWeight: 600, 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#f92955',
                  flexShrink: 0
                }} />
                Payment Type
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Card
                  elevation={0}
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    border: paymentForm.type === 'card' ? '2px solid #f92955' : '2px solid #BBFFC1',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    background: paymentForm.type === 'card' ? 'linear-gradient(135deg, #f9295515 0%, #BBFFC115 100%)' : '#BBFFC1',
                    '&:hover': {
                      borderColor: '#f92955',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(249, 41, 85, 0.15)',
                    }
                  }}
                  onClick={() => setPaymentForm({ ...paymentForm, type: 'card' })}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ mb: 2, fontSize: '2.5rem' }}>
                      💳
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000', mb: 1 }}>
                      Credit/Debit Card
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#000000', fontSize: '0.85rem' }}>
                      Visa, Mastercard, and other major cards
                    </Typography>
                    {paymentForm.type === 'card' && (
                      <Box sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: '#f92955',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}>
                        ✓
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    border: paymentForm.type === 'mobile_money' ? '2px solid #f92955' : '2px solid #BBFFC1',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    background: paymentForm.type === 'mobile_money' ? 'linear-gradient(135deg, #f9295515 0%, #BBFFC115 100%)' : '#BBFFC1',
                    '&:hover': {
                      borderColor: '#f92955',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(249, 41, 85, 0.15)',
                    }
                  }}
                  onClick={() => setPaymentForm({ ...paymentForm, type: 'mobile_money' })}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ mb: 2, fontSize: '2.5rem' }}>
                      📱
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000', mb: 1 }}>
                      Mobile Money
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#000000', fontSize: '0.85rem' }}>
                      Airtel Money, MTN Mobile Money
                    </Typography>
                    {paymentForm.type === 'mobile_money' && (
                      <Box sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: '#f92955',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}>
                        ✓
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {paymentForm.type && (
              <Box sx={{ 
                animation: 'slideIn 0.3s ease-out',
                '@keyframes slideIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)'
                  }
                }
              }}>
                {/* Account Holder Name */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ 
                    color: '#000000', 
                    fontWeight: 600, 
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#f92955',
                      flexShrink: 0
                    }} />
                    {paymentForm.type === 'card' ? 'Card Holder Name' : 'Account Holder Name'}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={paymentForm.type === 'card' ? 'John Doe' : 'John Doe'}
                    value={paymentForm.name}
                    onChange={(e) => setPaymentForm({ ...paymentForm, name: e.target.value })}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: '#fff',
                        '&:hover fieldset': {
                          borderColor: '#f92955',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f92955',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                          '& .MuiInputLabel-root': {
                            color: '#000000',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#000000',
                        '&.Mui-focused': {
                          color: '#000000',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Card/Phone Number */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ 
                    color: '#000000', 
                    fontWeight: 600, 
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#f92955',
                      flexShrink: 0
                    }} />
                    {paymentForm.type === 'card' ? 'Card Number' : 'Phone Number'}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={paymentForm.type === 'card' ? '1234 5678 9012 3456' : '+256 7XX XXX XXX'}
                    value={paymentForm.number}
                    onChange={(e) => setPaymentForm({ ...paymentForm, number: e.target.value })}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: '#fff',
                        '&:hover fieldset': {
                          borderColor: '#f92955',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f92955',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                          '& .MuiInputLabel-root': {
                            color: '#000000',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#000000',
                        '&.Mui-focused': {
                          color: '#000000',
                        },
                      },
                    }}
                  />
                </Box>

                {paymentForm.type === 'card' && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ 
                        color: '#000000', 
                        fontWeight: 600, 
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Box sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#f92955',
                          flexShrink: 0
                        }} />
                        Expiry Date
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="MM/YY"
                        value={paymentForm.expiry}
                        onChange={(e) => setPaymentForm({ ...paymentForm, expiry: e.target.value })}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: '#fff',
                            '&:hover fieldset': {
                              borderColor: '#f92955',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#f92955',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ 
                        color: '#000000', 
                        fontWeight: 600, 
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Box sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#f92955',
                          flexShrink: 0
                        }} />
                        CVV
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="123"
                        value={paymentForm.cvv}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cvv: e.target.value })}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: '#fff',
                            '&:hover fieldset': {
                              borderColor: '#f92955',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#f92955',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Provider Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ 
                    color: '#000000', 
                    fontWeight: 600, 
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#f92955',
                      flexShrink: 0
                    }} />
                    {paymentForm.type === 'card' ? 'Card Provider' : 'Mobile Money Provider'}
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={paymentForm.provider}
                      onChange={(e) => setPaymentForm({ ...paymentForm, provider: e.target.value })}
                      displayEmpty
                      sx={{
                        borderRadius: 3,
                        background: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f92955',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f92955',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f92955',
                          borderWidth: '2px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select {paymentForm.type === 'card' ? 'card provider' : 'mobile money provider'}</em>
                      </MenuItem>
                      {paymentForm.type === 'card' ? (
                        <>
                          <MenuItem value="visa">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h5">💳</Typography>
                              <Typography sx={{ color: '#000000' }}>Visa</Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="mastercard">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h5">💳</Typography>
                              <Typography sx={{ color: '#000000' }}>Mastercard</Typography>
                            </Box>
                          </MenuItem>
                        </>
                      ) : (
                        <>
                          <MenuItem value="airtel">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h5">📱</Typography>
                              <Typography sx={{ color: '#000000' }}>Airtel Money</Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="mtn">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h5">📱</Typography>
                              <Typography sx={{ color: '#000000' }}>MTN Mobile Money</Typography>
                            </Box>
                          </MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          gap: 2,
          background: 'linear-gradient(135deg, #BBFFC1 0%, #f92955 100%)',
          borderTop: '1px solid #BBFFC1'
        }}>
          <Button
            onClick={closeDialog}
            variant="outlined"
            sx={{
              borderColor: '#f92955',
              color: '#000000',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: '#BBFFC1',
                bgcolor: '#f9295515',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(249, 41, 85, 0.2)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingMethod ? handleEditPayment : handleAddPayment}
            variant="contained"
            disabled={!paymentForm.type || !paymentForm.name || !paymentForm.number || !paymentForm.provider}
            sx={{
              bgcolor: '#f92955',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              boxShadow: '0 4px 16px rgba(249, 41, 85, 0.3)',
              '&:hover': {
                bgcolor: '#BBFFC1',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(249, 41, 85, 0.4)',
              },
              '&:disabled': {
                bgcolor: '#f92955',
                boxShadow: 'none',
                transform: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {editingMethod ? 'Update Payment Method' : 'Add Payment Method'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
